import * as Sentry from "@sentry/angular";
import config from "../1config";
import { environment } from "../environments/environment";

const sentry_loader = function(){
  // localStorage override 체크
  const sentryEnabled = localStorage.getItem('chekt::sentry_enabled');
  if (sentryEnabled === 'false') {
    console.debug('⚠️ Sentry disabled by localStorage override');
    return;
  }

  if (!config.sentry_enabled || !config.sentry_dsn) {
    console.debug('⚠️ Sentry disabled in config');
    return;
  }

  // localhost 체크 - 로컬 개발 환경에서는 Sentry 비활성화
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.debug('⚠️ Sentry disabled on localhost');
    return;
  }

  Sentry.init({
    dsn: config.sentry_dsn,

    // 환경 설정
    environment: config.env,  // 'dev', 'qe', 'prod'
    // CI/CD에서 environment.prod.ts를 sed로 수정하여 값 주입
    // Local: environment.ts의 기본값 "local-dev"
    release: environment.sentryRelease,

    // v8+ Integration (함수형)
    integrations: [
      Sentry.browserTracingIntegration({
        // Angular Router 통합 자동 설정
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [
          window.location.origin,
          /^https:\/\/api\.chekt\.com/,
          /^https:\/\/api\.chektqe\.com/,
          /^https:\/\/api\.chektdev\.com/,
          /^https:\/\/dealer\.chekt\.com/,
        ],
        networkCaptureBodies: false,  // 보안: 요청/응답 바디 제외
      }),
    ],

    // Performance Monitoring (환경별 샘플링)
    // dev: 100%, qe: 50%, prod: 10%
    tracesSampleRate: config.env === 'prod' ? 0.1 : config.env === 'qe' ? 0.5 : 1.0,

    // Session Replay (환경별 샘플링)
    // dev: 10%, qe: 5%, prod: 1% (에러 시는 항상 100%)
    replaysSessionSampleRate: config.env === 'prod' ? 0.01 : config.env === 'qe' ? 0.05 : 0.1,
    replaysOnErrorSampleRate: 1.0,  // 에러 발생 시 100% 기록

    // 최대 Breadcrumbs 수 제한 (메모리 최적화)
    maxBreadcrumbs: 50,

    // 요청 바디 크기 제한 (성능 최적화)
    maxValueLength: 1000,

    // 에러 필터링
    ignoreErrors: [
      'Non-Error exception captured',
      'Non-Error promise rejection captured',
      /Loading chunk [\d]+ failed/,  // 청크 로딩 실패 - Webpack (GlobalErrorHandler에서 처리)
      /Failed to fetch dynamically imported module/,  // 청크 로딩 실패 - esbuild/Chrome (GlobalErrorHandler에서 처리)
      /Importing a module script failed/,  // 청크 로딩 실패 - esbuild/Safari (GlobalErrorHandler에서 처리)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],

    // 민감 정보 필터링
    beforeSend(event, hint) {
      // localhost에서 발생한 에러는 전송하지 않음 (추가 방어)
      if (event.request?.url?.includes('localhost')) {
        return null;
      }
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return null;
      }

      // 쿠키 및 Authorization 헤더 제거
      if (event.request) {
        delete event.request.cookies;
        if (event.request.headers) {
          delete event.request.headers['Authorization'];
          delete event.request.headers['Cookie'];
        }
      }

      // PII 데이터 마스킹
      if (event.message) {
        // 이메일 마스킹
        event.message = event.message.replace(
          /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
          '[email]'
        );

        // 전화번호 마스킹 (다양한 형식)
        event.message = event.message.replace(
          /\b(\+?\d{1,3}[-.]?)?\(?\d{3}\)?[-.]?\d{3}[-.]?\d{4}\b/g,
          '[phone]'
        );

        // IP 주소 마스킹
        event.message = event.message.replace(
          /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g,
          '[ip]'
        );

        // JWT 토큰 마스킹
        event.message = event.message.replace(
          /eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
          '[jwt]'
        );
      }

      // Exception 메시지도 마스킹
      if (event.exception?.values) {
        event.exception.values.forEach(exception => {
          if (exception.value) {
            exception.value = exception.value
              .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[email]')
              .replace(/\b(\+?\d{1,3}[-.]?)?\(?\d{3}\)?[-.]?\d{3}[-.]?\d{4}\b/g, '[phone]')
              .replace(/eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[jwt]');
          }
        });
      }

      // Custom Fingerprinting: 동일한 에러를 효과적으로 그룹화
      const originalException = hint?.originalException;
      if (originalException) {
        const errorMessage = (originalException as any).message || String(originalException);

        // HTTP 에러 그룹화 (status code 기준)
        const httpErrorMatch = errorMessage.match(/HTTP.*(\d{3})/);
        if (httpErrorMatch) {
          event.fingerprint = ['http-error', httpErrorMatch[1]];
        }

        // API 에러 그룹화 (endpoint + status code 기준)
        // status를 포함하지 않으면 같은 API의 401 노이즈와 500 장애가 한 이슈로 묶여버림
        const apiUrlMatch = errorMessage.match(/api\.(chekt|chektdev|chektqe)\.com\/([^\/]+)/);
        if (apiUrlMatch) {
          event.fingerprint = httpErrorMatch
            ? ['api-error', apiUrlMatch[2], httpErrorMatch[1]]
            : ['api-error', apiUrlMatch[2]];
        }

        // Angular 청크 로딩 실패 (이미 ignoreErrors에 있지만, 혹시 도달하면 그룹화)
        if (errorMessage.includes('Loading chunk') || errorMessage.includes('Failed to fetch dynamically imported module') || errorMessage.includes('Importing a module script failed')) {
          event.fingerprint = ['chunk-loading-error'];
        }

        // ResizeObserver 에러 그룹화
        if (errorMessage.includes('ResizeObserver')) {
          event.fingerprint = ['resize-observer-error'];
        }
      }

      return event;
    },
  });

  console.debug('✅ Sentry initialized', {
    env: config.env,
    dsn: config.sentry_dsn.substring(0, 30) + '...',
    release: environment.sentryRelease,
  });
}

export default sentry_loader;
